/**
 * Carga del script de Cloudflare Turnstile y wrapper de su API.
 *
 * El script se carga en modo `render=explicit` para que NO renderice
 * automáticamente al detectar `<div class="cf-turnstile">`. Renderizamos
 * nosotros mismos llamando a `window.turnstile.render(...)` cuando el
 * formulario esté pintado.
 *
 * Site key esperada en `VITE_TURNSTILE_SITE_KEY` (cargada por Vite desde
 * `web/.env`). En dev las testing keys públicas de Cloudflare devuelven
 * "success" siempre.
 */

const SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

interface TurnstileRenderOptions {
  sitekey: string;
  callback: (token: string) => void;
  'expired-callback'?: () => void;
  'error-callback'?: () => void;
  theme?: 'light' | 'dark' | 'auto';
  size?: 'normal' | 'compact';
}

interface TurnstileApi {
  render: (
    container: string | HTMLElement,
    options: TurnstileRenderOptions,
  ) => string;
  reset: (widgetId?: string) => void;
  getResponse: (widgetId?: string) => string | undefined;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let loadPromise: Promise<TurnstileApi> | null = null;

/** Carga el script de Turnstile (idempotente). Resuelve con la API global. */
export function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) {
    return Promise.resolve(window.turnstile);
  }
  if (loadPromise) return loadPromise;

  loadPromise = new Promise<TurnstileApi>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', () => {
      if (window.turnstile) {
        resolve(window.turnstile);
      } else {
        reject(new Error('Turnstile script cargó pero window.turnstile no existe.'));
      }
    });
    script.addEventListener('error', () => {
      reject(new Error('No se pudo cargar el script de Turnstile.'));
    });
    document.head.appendChild(script);
  });

  return loadPromise;
}
